import React from 'react';
import { FileText, AlertTriangle, CheckCircle, XCircle, Scale, Shield, Users, DollarSign } from 'lucide-react';

const TermsPage = () => {
  const sections = [
    {
      id: 'acceptance',
      title: '1. Acceptance of Terms',
      icon: CheckCircle,
      content: `By accessing or using HaulLogix ("the Platform"), you agree to be bound by these Terms of Service and all applicable laws and regulations. If you do not agree with any of these terms, you are prohibited from using or accessing this site. The materials contained in this website are protected by applicable copyright and trademark law.`
    },
    {
      id: 'eligibility',
      title: '2. Eligibility & Registration',
      icon: Users,
      content: `To use HaulLogix, you must:
      
• Be at least 18 years of age
• Have the legal authority to enter into binding contracts
• Provide accurate, current, and complete registration information
• Maintain the security of your account credentials
• Promptly update any changes to your information

For commercial users, you must maintain valid operating authority (MC/DOT numbers) and insurance coverage as required by law. You agree to provide documentation for verification upon request.`
    },
    {
      id: 'verification',
      title: '3. Verification Requirements',
      icon: Shield,
      content: `HaulLogix operates as a verified platform. By using our services, you agree to:

• Submit accurate business credentials and documentation
• Undergo identity and business verification processes
• Maintain current insurance certificates and operating authority
• Allow HaulLogix to verify information with third-party sources
• Accept that verification status may be revoked for inaccurate information

Verification status does not guarantee the financial stability, reliability, or performance of any user. Users are responsible for conducting their own due diligence before entering into business arrangements.`
    },
    {
      id: 'conduct',
      title: '4. User Conduct',
      icon: Scale,
      content: `You agree NOT to:

• Post false, misleading, or fraudulent load information
• Engage in double-brokering without proper disclosure
• Harass, threaten, or abuse other users
• Circumvent the platform to avoid fees or bypass safety features
• Use automated systems to scrape data or spam users
• Impersonate other users or businesses
• Share your account credentials with unauthorized parties
• Post loads you do not have authority to transport
• Engage in any illegal activity through the platform

Violations may result in account suspension or permanent ban, and we reserve the right to report illegal activity to law enforcement.`
    },
    {
      id: 'loads',
      title: '5. Load Posting & Transportation',
      icon: FileText,
      content: `When posting loads:

• All load information must be accurate and complete
• Pricing must be transparent and honor posted rates
• Loads must be legal to transport under applicable law
• You must have authority to arrange transportation

When accepting loads:

• You represent that you have appropriate authority and insurance
• You agree to transport loads according to stated terms
• You must communicate promptly regarding any issues
• You agree to complete required documentation (inspections, BOLs)

HaulLogix is a marketplace platform. We do not take possession of cargo, arrange insurance, or guarantee completion of any transaction.`
    },
    {
      id: 'payments',
      title: '6. Payments & Fees',
      icon: DollarSign,
      content: `Subscription fees are billed according to your selected plan. All fees are:

• Non-refundable except as required by law
• Subject to change with 30 days' notice
• Exclusive of applicable taxes

You agree to:

• Pay all fees associated with your account
• Maintain valid payment information
• Accept responsibility for any chargebacks or disputes

Payment disputes between users (shippers, brokers, carriers) are between those parties. HaulLogix does not mediate payment disputes but may provide transaction records upon legal request.`
    },
    {
      id: 'liability',
      title: '7. Limitation of Liability',
      icon: AlertTriangle,
      content: `TO THE MAXIMUM EXTENT PERMITTED BY LAW:

HaulLogix provides the platform "as is" without warranties of any kind. We do not guarantee:

• The accuracy of user-provided information
• The completion of any transaction
• The reliability or performance of any user
• Uninterrupted or error-free service

HaulLogix shall not be liable for:

• Direct, indirect, incidental, or consequential damages
• Lost profits, revenue, or data
• Damages arising from user conduct or third-party actions
• Cargo loss, damage, or delays

Our total liability for any claim shall not exceed the fees you paid to HaulLogix in the 12 months preceding the claim.`
    },
    {
      id: 'indemnification',
      title: '8. Indemnification',
      icon: Shield,
      content: `You agree to indemnify, defend, and hold harmless HaulLogix, its officers, directors, employees, agents, and affiliates from any claims, damages, losses, or expenses (including reasonable attorney fees) arising from:

• Your use of the platform
• Your violation of these Terms
• Your violation of any third-party rights
• Any content you post or transmit
• Your business dealings with other users
• Any cargo loss, damage, or injury related to your transportation activities`
    },
    {
      id: 'termination',
      title: '9. Termination',
      icon: XCircle,
      content: `HaulLogix may suspend or terminate your account at any time for:

• Violation of these Terms of Service
• Fraudulent or illegal activity
• Failure to maintain required credentials
• Non-payment of fees
• Any reason at our sole discretion

Upon termination:

• Your right to use the platform ceases immediately
• We may retain your data as required by law or for legitimate business purposes
• Outstanding fees remain due and payable
• These Terms survive termination where applicable`
    },
    {
      id: 'disputes',
      title: '10. Dispute Resolution',
      icon: Scale,
      content: `Any dispute arising from these Terms or your use of HaulLogix shall be:

• First attempted to be resolved through good-faith negotiation
• Subject to binding arbitration under the rules of the American Arbitration Association
• Conducted in Detroit, Michigan
• Governed by the laws of the State of Michigan

Class Action Waiver: You agree to resolve disputes individually and waive any right to participate in class actions against HaulLogix.

Small Claims: Either party may bring claims in small claims court if the claim qualifies.`
    },
    {
      id: 'changes',
      title: '11. Changes to Terms',
      icon: FileText,
      content: `HaulLogix reserves the right to modify these Terms at any time. We will provide notice of material changes by:

• Posting the updated Terms on the platform
• Sending email notification to registered users
• Displaying a prominent notice upon login

Continued use of the platform after changes constitutes acceptance of the modified Terms. If you do not agree to the changes, you must stop using the platform and may request account deletion.`
    }
  ];

  return (
    <div className="min-h-screen bg-[#0a0c10] pt-16">
      {/* Hero Section */}
      <div className="bg-gradient-to-b from-[#4ADE80]/10 to-transparent py-16">
        <div className="max-w-4xl mx-auto px-4 text-center">
          <h1 className="text-4xl md:text-5xl font-bold text-white mb-4 font-['Barlow_Condensed'] uppercase tracking-wide">
            Terms of <span className="text-[#4ADE80]">Service</span>
          </h1>
          <p className="text-xl text-gray-400 max-w-2xl mx-auto">
            Please read these terms carefully before using HaulLogix.
          </p>
          <p className="text-sm text-gray-500 mt-4">Effective Date: March 14, 2026</p>
        </div>
      </div>

      <div className="max-w-4xl mx-auto px-4 py-12">
        {/* Important Notice */}
        <div className="bg-[#4ADE80]/10 border border-[#4ADE80]/30 rounded-xl p-6 mb-8">
          <div className="flex items-start gap-3">
            <AlertTriangle className="w-6 h-6 text-[#4ADE80] flex-shrink-0 mt-0.5" />
            <div>
              <h3 className="font-semibold text-[#4ADE80] mb-1">Important Notice</h3>
              <p className="text-gray-300 text-sm">
                These Terms contain important information about your legal rights, including limitations 
                of liability and dispute resolution through arbitration. By using HaulLogix, you agree 
                to these Terms. If you do not agree, do not use the platform.
              </p>
            </div>
          </div>
        </div>

        {/* Table of Contents */}
        <div className="bg-[#12151a] rounded-xl border border-[#1a1d24] p-6 mb-8">
          <h3 className="text-lg font-semibold text-white mb-4">Table of Contents</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
            {sections.map((section) => (
              <a 
                key={section.id}
                href={`#${section.id}`}
                className="text-[#4ADE80] hover:underline text-sm"
              >
                {section.title}
              </a>
            ))}
          </div>
        </div>

        {/* Sections */}
        {sections.map((section) => (
          <section key={section.id} id={section.id} className="mb-6">
            <div className="bg-[#12151a] rounded-xl border border-[#1a1d24] p-8">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 rounded-lg bg-[#4ADE80]/20 flex items-center justify-center">
                  <section.icon className="w-5 h-5 text-[#4ADE80]" />
                </div>
                <h2 className="text-xl font-bold text-white">{section.title}</h2>
              </div>
              <div className="text-gray-400 leading-relaxed whitespace-pre-line">
                {section.content}
              </div>
            </div>
          </section>
        ))}

        {/* Contact Section */}
        <div className="bg-[#12151a] rounded-xl border border-[#1a1d24] p-8 text-center">
          <h2 className="text-xl font-bold text-white mb-4">Questions About These Terms?</h2>
          <p className="text-gray-400 mb-4">
            If you have any questions about these Terms of Service, please contact us:
          </p>
          <div className="space-y-2 text-gray-300">
            <p>Email: <a href="mailto:support@haullogixloads.com" className="text-[#4ADE80] hover:underline">support@haullogixloads.com</a></p>
            <p>Phone: <a href="tel:+13133142922" className="text-[#4ADE80] hover:underline">(313) 314-2922</a></p>
            <p>Location: Detroit, Michigan, USA</p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TermsPage;
